import React, { useEffect, useState } from "react";

export function venueSuggestionFromFeature(feature) {
  const properties = feature?.properties || {};
  const name = properties.name || properties.street || properties.locality || "";
  const addressParts = [
    [properties.housenumber, properties.street].filter(Boolean).join(" "),
    properties.locality,
    properties.district,
    properties.city,
    properties.state,
    properties.postcode,
    properties.country,
  ].filter(Boolean);
  const address = [...new Set(addressParts)].join(", ");

  return { name: name || address, address };
}

function venueSuggestionLabel(feature) {
  const { name, address } = venueSuggestionFromFeature(feature);
  return [name, address && address !== name ? address : ""].filter(Boolean).join(" · ");
}

export default function VenueSearchField({
  venue,
  city,
  onVenueChange,
  onAddressChange,
  ...inputProps
}) {
  const [query, setQuery] = useState(venue || "");
  const [suggestions, setSuggestions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [searched, setSearched] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  useEffect(() => {
    setQuery(venue || "");
  }, [venue]);

  useEffect(() => {
    const search = query.trim();
    if (!isOpen || search.length < 3 || !city?.trim()) {
      setSuggestions([]);
      setLoading(false);
      setError(false);
      setSearched(false);
      return undefined;
    }

    const controller = new AbortController();
    const timeout = window.setTimeout(async () => {
      setLoading(true);
      setError(false);
      setSearched(false);
      try {
        const params = new URLSearchParams({
          q: `${search}, ${city.trim()}, India`,
          limit: "6",
          lang: "en",
        });
        const response = await fetch(`https://photon.komoot.io/api/?${params}`, {
          signal: controller.signal,
        });
        if (!response.ok) throw new Error(`Venue search returned ${response.status}`);
        const payload = await response.json();
        setSuggestions(Array.isArray(payload.features) ? payload.features : []);
        setSearched(true);
      } catch (searchError) {
        if (searchError.name !== "AbortError") {
          setSuggestions([]);
          setError(true);
          setSearched(true);
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }, 350);

    return () => {
      window.clearTimeout(timeout);
      controller.abort();
    };
  }, [city, isOpen, query]);

  const selectSuggestion = (feature) => {
    const suggestion = venueSuggestionFromFeature(feature);
    setQuery(suggestion.name);
    setSuggestions([]);
    setIsOpen(false);
    setActiveIndex(-1);
    onVenueChange(suggestion.name);
    onAddressChange(suggestion.address);
  };

  return (
    <div className="city-search-field">
      <input
        {...inputProps}
        type="search"
        value={query}
        onChange={(event) => {
          const value = event.target.value;
          setQuery(value);
          setIsOpen(true);
          setActiveIndex(-1);
          onVenueChange(value);
          onAddressChange("");
        }}
        onFocus={() => setIsOpen(true)}
        onBlur={() => setIsOpen(false)}
        onKeyDown={(event) => {
          if (event.key === "ArrowDown" && suggestions.length) {
            event.preventDefault();
            setActiveIndex((index) => (index + 1) % suggestions.length);
          } else if (event.key === "ArrowUp" && suggestions.length) {
            event.preventDefault();
            setActiveIndex((index) => index <= 0 ? suggestions.length - 1 : index - 1);
          } else if (event.key === "Enter" && activeIndex >= 0 && suggestions[activeIndex]) {
            event.preventDefault();
            selectSuggestion(suggestions[activeIndex]);
          } else if (event.key === "Escape") {
            setIsOpen(false);
            setActiveIndex(-1);
          }
        }}
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={isOpen && suggestions.length > 0}
        aria-controls="booking-venue-suggestions"
        aria-activedescendant={activeIndex >= 0 ? `booking-venue-suggestion-${activeIndex}` : undefined}
        autoComplete="off"
      />
      {isOpen && loading && (
        <div className="city-search-suggestions text-muted fs-12" role="status">
          Searching venues near {city}…
        </div>
      )}
      {isOpen && error && (
        <div className="city-search-suggestions text-muted fs-12" role="status">
          Venue suggestions are temporarily unavailable. You can still enter the venue manually.
        </div>
      )}
      {isOpen && searched && !loading && !error && suggestions.length === 0 && (
        <div className="city-search-suggestions text-muted fs-12" role="status">
          No matching venues found near {city}. Try another spelling or enter the venue manually.
        </div>
      )}
      {isOpen && suggestions.length > 0 && (
        <ul className="city-search-suggestions" id="booking-venue-suggestions" role="listbox">
          {suggestions.map((feature, index) => (
            <li key={feature.properties?.osm_id || `${venueSuggestionLabel(feature)}-${index}`} role="presentation">
              <button
                type="button"
                id={`booking-venue-suggestion-${index}`}
                className={`city-search-suggestion${index === activeIndex ? " active" : ""}`}
                role="option"
                aria-selected={index === activeIndex}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => selectSuggestion(feature)}
              >
                {venueSuggestionLabel(feature)}
              </button>
            </li>
          ))}
          <li className="text-muted fs-11" role="presentation" style={{ padding: "6px 10px" }}>
            Venue data © OpenStreetMap contributors
          </li>
        </ul>
      )}
    </div>
  );
}
